import { callApi } from 'utils/api/callApi'
import { decamelizeKeysWithNumbers } from 'utils/api/decamelizeKeysWithNumbers'
import { FP_ENDPOINTS } from 'apps/fleet-planner/config'
import { ICustomerContact } from 'apps/fleet-planner/types/customers'

export const isFacility = (facility: any): facility is IFacility => {
  const { addresses, id, name } = facility
  return !!name && !!addresses && !!addresses.length && !!id
}

export enum FacilityPurpose {
  stopLocation = 'stop_location',
  facilityPrimary = 'facility_primary',
}

export interface IFacilityAddress {
  id?: string
  addressee?: string | null
  addressableId?: string
  addressLine1: string
  addressLine2?: string | null
  locality: string
  subdivisionCode: string
  countryCode: string
  postalCode?: string | null
  purpose: FacilityPurpose
  sourceAddressId?: string | null
}

export interface IGetFacilitiesParams {
  pageNumber?: number
  pageSize?: number
  sortOrder?: string
  sortField?: string
}

export interface IFacility {
  addresses: IFacilityAddress[]
  contacts: ICustomerContact[]
  name: string
  id: string
  updatedAt?: string
}

export interface ICreateNewFacilityRequest {
  facility: Omit<IFacility, 'id'>
}

export interface IGetRecentFacilitiesResponse {
  facilities: IFacility[]
  // total number of facilities
  totalCount: number
}

export interface ICreateNewFacilityResponse {
  facility: IFacility
}

export interface IUpdateFacilityAddressRequest extends IFacilityAddress {}

export interface IUpdateFacilityAddressResponse {
  address: IFacilityAddress
}

export interface IGetFacilityResponse {
  facility: IFacility
}

export interface ICreateFacilityContactRequest extends ICustomerContact {}
export interface ICreateFacilityContactResponse {
  contact: ICustomerContact
}

export interface IUpdateFacilityContactRequest extends ICustomerContact {}

export interface IUpdateFacilityContactResponse {
  contact: ICustomerContact
}

export interface IUpdateFacilityRequest {
  facility: IFacility
}
export interface IUpdateFacilityResponse extends ICreateNewFacilityResponse {}

export const getRecentFacilities = (
  params: IGetFacilitiesParams = {}
): Promise<IGetRecentFacilitiesResponse> =>
  callApi({
    endpoint: FP_ENDPOINTS.facilities,
    method: 'get',
    params,
  })

export const getFacility = (facilityId: string): Promise<IGetFacilityResponse> => {
  return callApi({
    endpoint: `${FP_ENDPOINTS.facilities}/${facilityId}`,
    method: 'get',
  })
}

export const createNewFacility = (
  body: ICreateNewFacilityRequest
): Promise<ICreateNewFacilityResponse> => {
  return callApi({
    endpoint: FP_ENDPOINTS.facilities,
    method: 'post',
    body: decamelizeKeysWithNumbers(body),
  })
}

export const updateFacilityAddress = (
  facilityId: string,
  addressId: string,
  body: IUpdateFacilityAddressRequest
): Promise<IUpdateFacilityAddressResponse> =>
  callApi({
    endpoint: `${FP_ENDPOINTS.facilities}/${facilityId}/addresses/${addressId}`,
    method: 'put',
    body: decamelizeKeysWithNumbers(body),
  })

export const updateFacility = (
  facilityId: string,
  body: IUpdateFacilityRequest
): Promise<IUpdateFacilityResponse> =>
  callApi({
    endpoint: `${FP_ENDPOINTS.facilities}/${facilityId}`,
    method: 'put',
    body: decamelizeKeysWithNumbers(body),
  })

export const deleteFacility = (facilityId: string): Promise<void> =>
  callApi({
    endpoint: `${FP_ENDPOINTS.facilities}/${facilityId}`,
    method: 'delete',
  })

export const createFacilityContact = (
  facilityId: string,
  body: ICreateFacilityContactRequest
): Promise<ICreateFacilityContactResponse> => {
  return callApi({
    endpoint: `${FP_ENDPOINTS.facilities}/${facilityId}/contacts`,
    method: 'post',
    body: decamelizeKeysWithNumbers(body),
  })
}

export const updateFacilityContact = (
  facilityId: string,
  contactId: string,
  body: IUpdateFacilityContactRequest
): Promise<IUpdateFacilityContactResponse> => {
  return callApi({
    endpoint: `${FP_ENDPOINTS.facilities}/${facilityId}/contacts/${contactId}`,
    method: 'put',
    body: decamelizeKeysWithNumbers(body),
  })
}
